<script lang="ts">
  /* Reusable "Screen layout settings" modal — picks which data-table columns
     are shown, and in what order.

     Two lists, deliberately separate (owner, 2026-09-28: «отделить то что
     уже выбрано, от того что можно выбирать»):

     • **Chosen** lists only the columns currently on screen, in a panel of
       its own. It is the one place they are dragged into a different
       sequence, and a column leaves the table by its ×.
     • **Available** offers only what is not on screen yet — a tick moves the
       column up into the chosen panel. It is grouped under the same headings,
       in the same sequence, as the product card, so finding "Saturated fat"
       means remembering where it sits on the card rather than scanning eighty
       checkboxes. Its order is fixed for exactly that reason — a picker that
       reshuffles itself as you reorder columns is a picker you can no longer
       navigate from memory.

     Designed to be reused across tables (products, orders, customers…). */
  import { _ } from "svelte-i18n";

  export type ColumnDef = {
    key: string;
    label: string;
    locked?: boolean;
    /** Section heading this column is filed under in the picker. */
    group?: string;
  };

  import type { Snippet } from "svelte";

  let {
    open = $bindable(false),
    columns,
    visible = $bindable({}),
    /** Column keys, most-left first. Keys absent from it keep `columns` order. */
    order = $bindable<string[]>([]),
    title,
    children,
    onchange,
    onreorder,
  }: {
    open: boolean;
    columns: ColumnDef[];
    visible: Record<string, boolean>;
    order?: string[];
    title?: string;
    /* Optional content rendered above the column list — e.g. filter/sort controls. */
    children?: Snippet;
    /* Fired when the user toggles a column checkbox (a manual edit, distinct
       from the caller programmatically replacing `visible` via a preset). */
    onchange?: (visible: Record<string, boolean>) => void;
    /* Fired when the user drags or nudges a column into a new position. */
    onreorder?: (order: string[]) => void;
  } = $props();

  function close() {
    open = false;
  }

  function toggle(key: string) {
    visible = { ...visible, [key]: !visible[key] };
    onchange?.(visible);
  }

  /** Rank a column by its saved position, falling back to the caller's order
   *  so a column added since the user last customised lands at the end. */
  function rank(key: string): number {
    const i = order.indexOf(key);
    return i === -1 ? columns.findIndex((c) => c.key === key) + order.length : i;
  }

  /** The on-screen columns, left to right — the reorder list. */
  const shown = $derived(
    columns
      .filter((c, i) => (c.locked || visible[c.key]) && columns.findIndex((o) => o.key === c.key) === i)
      .sort((a, b) => rank(a.key) - rank(b.key)),
  );

  /** Move `key` to sit at index `to` among the shown columns, and write the
   *  result back as a full ordering of every column (hidden ones keep their
   *  relative places, so re-showing one does not drop it to the far right). */
  function moveTo(key: string, to: number) {
    const keys = shown.map((c) => c.key);
    const from = keys.indexOf(key);
    if (from === -1 || to < 0 || to >= keys.length || to === from) return;
    keys.splice(to, 0, ...keys.splice(from, 1));
    const hidden = [...new Set([...columns].sort((a, b) => rank(a.key) - rank(b.key)).map((c) => c.key))];
    order = [...keys, ...hidden.filter((k) => !keys.includes(k))];
    onreorder?.(order);
  }

  const move = (key: string, delta: number) =>
    moveTo(key, shown.findIndex((c) => c.key === key) + delta);

  // ── Drag to reorder ────────────────────────────────────────────────────
  let dragKey = $state<string | null>(null);
  let dropIdx = $state<number | null>(null);

  function onDragStart(e: DragEvent, key: string) {
    dragKey = key;
    // Firefox starts no drag at all unless the event carries data.
    e.dataTransfer?.setData("text/plain", key);
    if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
  }

  function onDragOver(e: DragEvent, idx: number) {
    if (!dragKey) return;
    e.preventDefault();
    dropIdx = idx;
  }
  function onDrop() {
    if (dragKey !== null && dropIdx !== null) moveTo(dragKey, dropIdx);
    dragKey = null;
    dropIdx = null;
  }

  /** The picker, split into the card's sections — the columns not on screen
   *  yet. A section with nothing left to add drops out. Columns with no group
   *  fall into a trailing unlabelled run rather than vanishing. */
  const grouped = $derived.by(() => {
    const out: { group: string | undefined; cols: ColumnDef[] }[] = [];
    for (const col of columns) {
      if (col.locked || visible[col.key]) continue;
      const last = out[out.length - 1];
      if (last && last.group === col.group) last.cols.push(col);
      else out.push({ group: col.group, cols: [col] });
    }
    return out;
  });
</script>

{#if open}
  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
  <div class="modal-overlay" role="presentation" onclick={close}>
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div
      class="cs-modal"
      role="dialog"
      aria-modal="true"
      aria-label={title ?? $_("pl.columns.title")}
      tabindex="-1"
      onclick={(e) => e.stopPropagation()}
    >
      <div class="cs-modal__header">
        <div class="cs-modal__title-wrap">
          <span class="material-icons-outlined modal-icon" aria-hidden="true">view_column</span>
          <h2 class="modal-title">{title ?? $_("pl.columns.title")}</h2>
        </div>
        <button type="button" class="modal-close" onclick={close} aria-label={$_("common.close")}>
          <span class="material-icons-outlined" aria-hidden="true">close</span>
        </button>
      </div>

      <div class="cs-modal__body">
        {#if children}
          {@render children()}
          <div class="cs-divider"></div>
        {/if}

        <div class="cs-chosen">
          <div class="cs-subhead">
            {$_("pl.columns.chosenLabel")}
            <span class="cs-count">{shown.length}</span>
          </div>
          <p class="cs-hint">{$_("pl.columns.orderHint")}</p>
          <ul class="cs-order">
            {#each shown as col, i (col.key)}
              <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
              <li
                class="cs-order-row"
                class:locked={col.locked}
                class:dragging={dragKey === col.key}
                class:drop-target={dropIdx === i && dragKey !== null && dragKey !== col.key}
                draggable="true"
                ondragstart={(e) => onDragStart(e, col.key)}
                ondragover={(e) => onDragOver(e, i)}
                ondrop={(e) => { e.preventDefault(); onDrop(); }}
                ondragend={() => { dragKey = null; dropIdx = null; }}
              >
                <span class="material-icons-outlined cs-grip" aria-hidden="true">drag_indicator</span>
                <span class="cs-order-label">{col.label}</span>
                <button
                  type="button"
                  class="cs-nudge"
                  disabled={i === 0}
                  onclick={() => move(col.key, -1)}
                  aria-label={$_("pl.columns.moveUp")}
                  title={$_("pl.columns.moveUp")}
                >
                  <span class="material-icons-outlined" aria-hidden="true">arrow_upward</span>
                </button>
                <button
                  type="button"
                  class="cs-nudge"
                  disabled={i === shown.length - 1}
                  onclick={() => move(col.key, 1)}
                  aria-label={$_("pl.columns.moveDown")}
                  title={$_("pl.columns.moveDown")}
                >
                  <span class="material-icons-outlined" aria-hidden="true">arrow_downward</span>
                </button>
                {#if col.locked}
                  <span class="cs-nudge-gap" aria-hidden="true"></span>
                {:else}
                  <button
                    type="button"
                    class="cs-nudge cs-hide"
                    onclick={() => toggle(col.key)}
                    aria-label={$_("pl.columns.hide")}
                    title={$_("pl.columns.hide")}
                  >
                    <span class="material-icons-outlined" aria-hidden="true">close</span>
                  </button>
                {/if}
              </li>
            {/each}
          </ul>
        </div>

        <div class="cs-subhead cs-subhead--available">{$_("pl.columns.availableLabel")}</div>
        {#if !grouped.length}
          <p class="cs-hint">{$_("pl.columns.allShown")}</p>
        {/if}
        {#each grouped as g}
          {#if g.group}
            <div class="cs-group">{g.group}</div>
          {/if}
          {#each g.cols as col}
            <label class="cs-row">
              <input type="checkbox" checked={false} onchange={() => toggle(col.key)} />
              <span>{col.label}</span>
            </label>
          {/each}
        {/each}
      </div>

      <div class="cs-modal__footer">
        <button type="button" class="btn btn--primary" onclick={close}>
          {$_("common.done")}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(48, 56, 65, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
  }
  .cs-modal {
    background: #fff;
    border-radius: 8px;
    width: 100%;
    max-width: 380px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  }
  .cs-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--color-divider);
  }
  .cs-modal__title-wrap { display: flex; align-items: center; gap: 10px; }
  .modal-icon { color: var(--color-primary); }
  .modal-title { margin: 0; font-size: 16px; font-weight: 700; color: var(--color-surface-darker); }
  .modal-close {
    background: none; border: none; cursor: pointer; color: var(--color-text-muted);
    display: flex; padding: 4px; border-radius: 4px;
  }
  .modal-close:hover { background: var(--color-bg); }

  /* Rows at about 24px (owner, 2026-09-28: «межстрочное расстояние чуть-чуть
     меньше»): eighty columns put the section headings a long scroll apart. */
  .cs-modal__body { padding: 12px 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 0; }
  .cs-divider { height: 1px; background: var(--color-divider); margin: 10px 0 4px; }
  .cs-subhead {
    display: flex; align-items: center; gap: 6px;
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--color-text-muted); padding: 3px 6px;
  }
  .cs-subhead--available { margin-top: 14px; }
  .cs-count {
    font-size: 10.5px; font-weight: 700; letter-spacing: 0; color: var(--color-primary);
    background: #fff; border: 1px solid var(--color-divider); border-radius: 9px; padding: 0 6px; line-height: 16px;
  }
  .cs-hint { margin: 0; padding: 0 6px 4px; font-size: 11.5px; color: var(--color-text-muted); line-height: 1.4; }
  /* What is on screen sits in a panel of its own, apart from what can be added. */
  .cs-chosen {
    background: var(--color-primary-soft, rgba(33, 133, 213, 0.06));
    border: 1px solid rgba(33, 133, 213, 0.25); border-radius: 8px; padding: 8px 6px 6px;
  }
  /* Each section: bold capitals and a thin grey rule above it. */
  .cs-group {
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
    color: var(--color-surface-darker); padding: 7px 6px 2px; margin-top: 6px;
    border-top: 1px solid var(--color-divider);
  }
  .cs-subhead--available + .cs-group { border-top: 0; margin-top: 0; }
  .cs-row {
    display: flex; align-items: center; gap: 10px;
    padding: 2px 6px; border-radius: 4px; cursor: pointer; font-size: 13.5px; line-height: 1.2;
  }
  .cs-row:hover { background: var(--color-bg); }
  .cs-row input { width: 15px; height: 15px; margin: 2px 0; cursor: inherit; }

  /* Reorder list */
  .cs-order { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
  .cs-order-row {
    display: flex; align-items: center; gap: 4px;
    padding: 1px 6px; border-radius: 4px; font-size: 13.5px; line-height: 1.2;
    border: 1px solid transparent; cursor: grab; background: transparent;
  }
  .cs-order-row:hover { background: #fff; }
  .cs-order-row.dragging { opacity: 0.45; }
  .cs-order-row.drop-target { border-color: var(--color-primary); background: rgba(33, 133, 213, 0.07); }
  .cs-grip { color: var(--color-text-muted); font-size: 18px !important; flex-shrink: 0; }
  .cs-order-label { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  /* Always on (the name): greyed, as a disabled tick would be. */
  .cs-order-row.locked .cs-order-label { color: var(--color-text-muted); }
  .cs-nudge {
    border: none; background: none; cursor: pointer; color: var(--color-text-muted);
    display: flex; padding: 2px; border-radius: 4px;
  }
  .cs-nudge:hover:not(:disabled) { color: var(--color-primary); background: rgba(33, 133, 213, 0.1); }
  .cs-nudge:disabled { opacity: 0.25; cursor: default; }
  .cs-nudge .material-icons-outlined { font-size: 16px !important; }
  .cs-hide:hover:not(:disabled) { color: var(--color-danger, #c62828); background: rgba(198, 40, 40, 0.08); }
  .cs-nudge-gap { width: 20px; flex-shrink: 0; }

  .cs-modal__footer {
    padding: 12px 20px; border-top: 1px solid var(--color-divider);
    display: flex; justify-content: flex-end;
  }
</style>
